import {
    FunctionComponent,
    ReactElement,
    useCallback,
    useState,
} from "react";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Icon,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
    styled,
} from "@mui/material";

import { LoadingButton } from "@mui/lab";
import { Add, Delete, DragIndicator, Edit, ExpandLess, ExpandMore } from "@mui/icons-material";

import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";

import {
    useGetBlocks,
    useCreateBlock,
    useUpdateBlock,
    useDeleteBlock,
    ICreateBlockMutationParameters,
} from "../../hooks";
import { ContentBlockType, IContentBlockNode } from "../../sdk/types/api";
import { useMessage } from "../../sdk/hooks";

const BLOCK_TYPES: ContentBlockType[] = [
    "heading_1", "heading_2", "heading_3",
    "paragraph", "rich_text", "image", "field", "list_item",
    "section", "faq", "benefit", "usp_group", "usp_detail",
    "feature", "offer", "care", "manufacturer",
];

const BLOCK_TYPE_LABELS: Record<ContentBlockType, string> = {
    heading_1: "Heading 1",
    heading_2: "Heading 2",
    heading_3: "Heading 3",
    paragraph: "Paragraph",
    rich_text: "Rich Text",
    image: "Image",
    field: "Field",
    list_item: "List Item",
    section: "Section",
    faq: "FAQ",
    benefit: "Benefit",
    usp_group: "USP Group",
    usp_detail: "USP Detail",
    feature: "Feature",
    offer: "Offer",
    care: "Care",
    manufacturer: "Manufacturer",
};

const TreeContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(0.5)};
`;

const BlockRow = styled("div")<{ depth: number }>`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    padding: ${({ theme }) => theme.spacing(0.75, 1)};
    margin-left: ${({ theme, depth }) => theme.spacing(depth * 3)};

    border: 1px solid #e0e0e0;
    border-radius: ${({ theme }) => theme.spacing(0.75)};
    background-color: #fafafa;

    &:hover {
        background-color: #f0f0f0;
    }
`;

const BlockTypeChip = styled("span")`
    font-size: 11px;
    font-weight: 500;
    color: #666;
    background-color: #ebebeb;
    border-radius: 4px;
    padding: 2px 6px;
    white-space: nowrap;
    text-transform: capitalize;
`;

const BlockLabel = styled(Typography)`
    flex: 1;
    font-size: 13px;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const BlockActions = styled("div")`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(0.25)};
    flex-shrink: 0;
`;

const AddBlockRow = styled("div")<{ depth: number }>`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    margin-left: ${({ theme, depth }) => theme.spacing(depth * 3)};
    padding-left: ${({ theme }) => theme.spacing(1)};
`;

const EmptyState = styled("div")`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    padding: ${({ theme }) => theme.spacing(4, 2)};
    color: #999;
    border: 1px dashed #d9d9d9;
    border-radius: ${({ theme }) => theme.spacing(1)};
`;

const PropertiesTextField = styled(TextField)`
    font-family: ${({ theme }) => theme.typography.fontFamily};
    font-size: 13px;
`;

export interface IContentBlocksSectionProps {
    productId: string;
    variantId: string;
}

interface IBlockFormState {
    type: ContentBlockType;
    properties: string;
}

interface IDialogState {
    open: boolean;
    mode: "create" | "edit";
    parentId?: string;
    afterId?: string | null;
    blockId?: string;
    initialValues: IBlockFormState;
}

const defaultDialogState = (): IDialogState => ({
    open: false,
    mode: "create",
    initialValues: { type: "section", properties: "{}" },
});

const blocksQueryKey = (variantId: string) => ["content-blocks", variantId];

function getBlockLabel(block: IContentBlockNode): string {
    const props = block.properties;
    if (typeof props.question === "string" && props.question) return props.question;
    if (typeof props.value === "string" && props.value) return props.value;
    if (typeof props.title === "string" && props.title) return props.title;
    if (typeof props.label === "string" && props.label) return props.label;
    if (typeof props.url === "string" && props.url) return props.url;
    return block.id.slice(0, 8);
}

interface IBlockTreeNodeProps {
    node: IContentBlockNode;
    index: number;
    depth: number;
    productId: string;
    variantId: string;
    onOpenCreate: (parentId: string, afterId?: string | null) => void;
    onOpenEdit: (block: IContentBlockNode) => void;
    onDelete: (blockId: string) => void;
    isDeleting: boolean;
}

const BlockTreeNode: FunctionComponent<IBlockTreeNodeProps> = ({
    node,
    index,
    depth,
    productId,
    variantId,
    onOpenCreate,
    onOpenEdit,
    onDelete,
    isDeleting,
}): ReactElement => {
    const [expanded, setExpanded] = useState(true);
    const hasChildren = node.children.length > 0;

    return (
        <Draggable draggableId={node.id} index={index}>
            {(provided) => (
                <div ref={provided.innerRef} {...provided.draggableProps}>
                    <BlockRow depth={depth}>
                        <IconButton size="small" {...provided.dragHandleProps} sx={{ cursor: "grab", padding: "2px" }}>
                            <DragIndicator fontSize="small" htmlColor="#bdbdbd" />
                        </IconButton>

                        {hasChildren ? (
                            <IconButton size="small" sx={{ padding: "2px" }} onClick={() => setExpanded((e) => !e)}>
                                {expanded ? (
                                    <ExpandLess fontSize="small" htmlColor="#999" />
                                ) : (
                                    <ExpandMore fontSize="small" htmlColor="#999" />
                                )}
                            </IconButton>
                        ) : (
                            <div style={{ width: 24 }} />
                        )}

                        <BlockTypeChip>{BLOCK_TYPE_LABELS[node.type]}</BlockTypeChip>
                        <BlockLabel variant="body2">{getBlockLabel(node)}</BlockLabel>

                        <BlockActions>
                            <Tooltip title="Add child block" enterDelay={500}>
                                <IconButton
                                    size="small"
                                    sx={{ padding: "2px" }}
                                    onClick={() => onOpenCreate(node.id, undefined)}>
                                    <Add fontSize="small" htmlColor="#666" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit block" enterDelay={500}>
                                <IconButton
                                    size="small"
                                    sx={{ padding: "2px" }}
                                    onClick={() => onOpenEdit(node)}>
                                    <Edit fontSize="small" htmlColor="#666" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete block and all children" enterDelay={500}>
                                <IconButton
                                    size="small"
                                    sx={{ padding: "2px" }}
                                    onClick={() => onDelete(node.id)}
                                    disabled={isDeleting}>
                                    <Delete fontSize="small" htmlColor="#e57373" />
                                </IconButton>
                            </Tooltip>
                        </BlockActions>
                    </BlockRow>

                    {expanded && hasChildren && (
                        <Droppable droppableId={node.id} type={`level-${depth + 1}`}>
                            {(childProvided) => (
                                <div
                                    ref={childProvided.innerRef}
                                    {...childProvided.droppableProps}>
                                    {node.children.map((child, childIndex) => (
                                        <BlockTreeNode
                                            key={child.id}
                                            node={child}
                                            index={childIndex}
                                            depth={depth + 1}
                                            productId={productId}
                                            variantId={variantId}
                                            onOpenCreate={onOpenCreate}
                                            onOpenEdit={onOpenEdit}
                                            onDelete={onDelete}
                                            isDeleting={isDeleting}
                                        />
                                    ))}
                                    {childProvided.placeholder}
                                    <AddBlockRow depth={depth + 1}>
                                        <Button
                                            size="small"
                                            startIcon={<Add />}
                                            sx={{ fontSize: 11, color: "#999" }}
                                            onClick={() => onOpenCreate(node.id, undefined)}>
                                            Add child
                                        </Button>
                                    </AddBlockRow>
                                </div>
                            )}
                        </Droppable>
                    )}
                </div>
            )}
        </Draggable>
    );
};

export const ContentBlocksSection: FunctionComponent<IContentBlocksSectionProps> = ({
    productId,
    variantId,
}): ReactElement => {
    const message = useMessage();
    const queryKey = blocksQueryKey(variantId);

    const blocksQuery = useGetBlocks(queryKey, productId, variantId, {
        enabled: Boolean(productId && variantId),
    });

    const createMutation = useCreateBlock(queryKey);
    const updateMutation = useUpdateBlock(queryKey);
    const deleteMutation = useDeleteBlock(queryKey);

    const [dialog, setDialog] = useState<IDialogState>(defaultDialogState());

    const handleCloseDialog = useCallback(() => {
        setDialog(defaultDialogState());
    }, []);

    const [formValues, setFormValues] = useState<IBlockFormState>({
        type: "section",
        properties: "{}",
    });

    const handleOpenDialogEffect = useCallback(
        (nextDialog: IDialogState) => {
            setFormValues(nextDialog.initialValues);
            setDialog(nextDialog);
        },
        [],
    );

    const openCreate = useCallback(
        (parentId?: string, afterId?: string | null) => {
            const next: IDialogState = {
                open: true,
                mode: "create",
                parentId,
                afterId,
                initialValues: { type: "section", properties: "{}" },
            };
            handleOpenDialogEffect(next);
        },
        [handleOpenDialogEffect],
    );

    const openEdit = useCallback(
        (block: IContentBlockNode) => {
            const next: IDialogState = {
                open: true,
                mode: "edit",
                blockId: block.id,
                initialValues: {
                    type: block.type,
                    properties: JSON.stringify(block.properties, null, 2),
                },
            };
            handleOpenDialogEffect(next);
        },
        [handleOpenDialogEffect],
    );

    const handleDelete = useCallback(
        (blockId: string) => {
            deleteMutation.mutate(
                { productId, variantId, blockId },
                {
                    onSuccess: () => message.showSuccess("Block deleted"),
                    onError: (error) => message.showError(error),
                },
            );
        },
        [deleteMutation, productId, variantId, message],
    );

    const isPropertiesValid = useCallback((value: string): boolean => {
        try {
            JSON.parse(value);
            return true;
        } catch {
            return false;
        }
    }, []);

    const handleDialogSave = useCallback(() => {
        if (!isPropertiesValid(formValues.properties)) {
            message.showError("Properties must be valid JSON");
            return;
        }

        const properties = JSON.parse(formValues.properties);

        if (dialog.mode === "create") {
            const params: ICreateBlockMutationParameters = {
                productId,
                variantId,
                type: formValues.type,
                properties,
                parentId: dialog.parentId,
                afterId: dialog.afterId,
            };

            createMutation.mutate(params, {
                onSuccess: () => {
                    message.showSuccess("Block created successfully");
                    handleCloseDialog();
                },
                onError: (error) => message.showError(error),
            });
        } else {
            updateMutation.mutate(
                {
                    productId,
                    variantId,
                    blockId: dialog.blockId ?? "",
                    properties,
                },
                {
                    onSuccess: () => {
                        message.showSuccess("Block updated successfully");
                        handleCloseDialog();
                    },
                    onError: (error) => message.showError(error),
                },
            );
        }
    }, [
        dialog,
        formValues,
        productId,
        variantId,
        createMutation,
        updateMutation,
        message,
        handleCloseDialog,
        isPropertiesValid,
    ]);

    const handleDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination) return;
            if (
                result.destination.droppableId === result.source.droppableId &&
                result.destination.index === result.source.index
            ) {
                return;
            }

            const blocks = blocksQuery.data ?? [];
            const blockId = result.draggableId;
            const destDroppableId = result.destination.droppableId;
            const destIndex = result.destination.index;

            // Find the sibling array in the destination droppable
            const isRoot = destDroppableId === "root";
            let siblings: IContentBlockNode[] = [];

            if (isRoot) {
                siblings = blocks;
            } else {
                const findChildren = (nodes: IContentBlockNode[]): IContentBlockNode[] | null => {
                    for (const node of nodes) {
                        if (node.id === destDroppableId) return node.children;
                        const found = findChildren(node.children);
                        if (found) return found;
                    }
                    return null;
                };
                siblings = findChildren(blocks) ?? [];
            }

            // afterId semantics: null = prepend, undefined = append, id = insert after sibling
            let afterId: string | null | undefined;

            if (destIndex === 0) {
                afterId = null; // prepend
            } else {
                // The sibling at destIndex - 1 (after removing the dragged item from source if same droppable)
                const siblingsWithoutSelf = siblings.filter((s) => s.id !== blockId);
                afterId = siblingsWithoutSelf[destIndex - 1]?.id ?? undefined;
            }

            const newParentId = isRoot ? null : destDroppableId;

            updateMutation.mutate(
                {
                    productId,
                    variantId,
                    blockId,
                    parentId: newParentId ?? undefined,
                    afterId,
                },
                {
                    onError: (error) => message.showError(error),
                },
            );
        },
        [blocksQuery.data, updateMutation, productId, variantId, message],
    );

    const blocks = blocksQuery.data ?? [];
    const isMutating =
        createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading;

    return (
        <>
            <DragDropContext onDragEnd={handleDragEnd}>
                {blocks.length === 0 && !blocksQuery.isLoading ? (
                    <EmptyState>
                        <Icon style={{ fontSize: 36, color: "#ccc" }}>widgets</Icon>
                        <Typography variant="body2" color="textSecondary">
                            No content blocks yet
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => openCreate(undefined, undefined)}>
                            Add Block
                        </Button>
                    </EmptyState>
                ) : (
                    <>
                        <Droppable droppableId="root" type="level-0">
                            {(provided) => (
                                <TreeContainer
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}>
                                    {blocks.map((block, index) => (
                                        <BlockTreeNode
                                            key={block.id}
                                            node={block}
                                            index={index}
                                            depth={0}
                                            productId={productId}
                                            variantId={variantId}
                                            onOpenCreate={openCreate}
                                            onOpenEdit={openEdit}
                                            onDelete={handleDelete}
                                            isDeleting={deleteMutation.isLoading}
                                        />
                                    ))}
                                    {provided.placeholder}
                                </TreeContainer>
                            )}
                        </Droppable>

                        <AddBlockRow depth={0} style={{ marginTop: 8 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Add />}
                                onClick={() => openCreate(undefined, undefined)}>
                                Add Root Block
                            </Button>
                        </AddBlockRow>
                    </>
                )}
            </DragDropContext>

            <Dialog open={dialog.open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle>
                    {dialog.mode === "create" ? "Add Block" : "Edit Block"}
                </DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Block Type</InputLabel>
                        <Select
                            label="Block Type"
                            value={formValues.type}
                            disabled={dialog.mode === "edit"}
                            onChange={(e) =>
                                setFormValues((v) => ({
                                    ...v,
                                    type: e.target.value as ContentBlockType,
                                }))
                            }>
                            {BLOCK_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {BLOCK_TYPE_LABELS[t]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <PropertiesTextField
                        label="Properties (JSON)"
                        multiline
                        rows={10}
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={formValues.properties}
                        onChange={(e) =>
                            setFormValues((v) => ({ ...v, properties: e.target.value }))
                        }
                        error={!isPropertiesValid(formValues.properties)}
                        helperText={
                            !isPropertiesValid(formValues.properties)
                                ? "Must be valid JSON"
                                : undefined
                        }
                        inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} size="small">
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        size="small"
                        onClick={handleDialogSave}
                        loading={isMutating}
                        disabled={!isPropertiesValid(formValues.properties)}>
                        {dialog.mode === "create" ? "Create" : "Save"}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
};
